/**
 * One serialized transition coordinator for walkthrough navigation.
 *
 * - Every Next/Back/jump/exit is a request with identity (session + generation).
 *   Only one transition runs at a time; a newer request supersedes older ones
 *   and aborts their AbortSignal so in-flight port work stops writing.
 * - `dataClean` is cleared BEFORE any write, so a superseded or failed write can
 *   never be trusted as the committed chapter; the next transition rebuilds.
 * - After every transition the coordinator drains in-flight port work, so an old
 *   reset can never overlap a new transition, exit or start.
 * - Interactive checkpoints: a chapter can require a real in-app decision. The
 *   chapter commits in 'awaiting' state; the decision is confirmed from saved
 *   data, then the chapter is finalized. Navigating away instead rebuilds.
 * - exit() is idempotent: duplicate calls share one restore.
 */
import { drainDemo, type DemoAck } from './demoBus';
export type { DemoAck } from './demoBus';

export interface PortContext { isCurrent: () => boolean; signal: AbortSignal }

export interface TransitionPorts {
  /** Clear transient UI (panels, theater, check-in). */
  clearUi(ctx: PortContext): Promise<DemoAck>;
  /** Put the exact scenario rows back to the pre-walkthrough snapshot. */
  resetBaseline(ctx: PortContext): Promise<DemoAck>;
  /** Persist chapter `index`. `fromIndex` null = after a reset. Checkpoint chapters persist their pre-decision state. */
  persistChapter(index: number, fromIndex: number | null, ctx: PortContext): Promise<DemoAck>;
  /** Refetch and confirm the app shows the saved rows for `index`. */
  refetch(index: number, ctx: PortContext): Promise<DemoAck>;
  /** Switch presentation actor and confirm the app reflects it. */
  switchActor(index: number, ctx: PortContext): Promise<DemoAck>;
  /** Apply and confirm the destination chapter's UI. */
  applyUi(index: number, ctx: PortContext): Promise<DemoAck>;
  /** True when chapter `index` needs a real in-app decision. */
  isCheckpoint(index: number): boolean;
  /** Persist the post-decision state after the decision is confirmed. */
  finalizeCheckpoint(index: number, ctx: PortContext): Promise<DemoAck>;
  /** Restore the pre-walkthrough state on exit. */
  restore(): Promise<DemoAck>;
  /** Optional: await extra in-flight work (defaults to the demo bus). */
  drain?(): Promise<void>;
}

export type TransitionStatus = 'idle' | 'transitioning' | 'awaiting' | 'error' | 'exiting' | 'exited';

export interface TransitionState {
  sessionId: string;
  status: TransitionStatus;
  committedIndex: number;
  targetIndex: number | null;
  error: string | null;
  failedOp: 'goto' | 'exit' | null;
  generation: number;
}

type Listener = (s: TransitionState) => void;

export class DemoTransitionCoordinator {
  private state: TransitionState;
  private listeners = new Set<Listener>();
  private chain: Promise<unknown> = Promise.resolve();
  private disposed = false;
  private dataClean = false;
  private abort: AbortController | null = null;
  private exitPromise: Promise<DemoAck> | null = null;

  constructor(private readonly ports: TransitionPorts, private readonly total: number, sessionId: string = crypto.randomUUID()) {
    this.state = { sessionId, status: 'idle', committedIndex: -1, targetIndex: null, error: null, failedOp: null, generation: 0 };
  }

  get snapshot(): TransitionState { return this.state; }
  /** Whether the persisted data is trusted to equal the committed chapter. */
  get isDataClean(): boolean { return this.dataClean; }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.state);
    return () => { this.listeners.delete(fn); };
  }

  private set(patch: Partial<TransitionState>) {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l(this.state);
  }

  get busy(): boolean {
    return this.state.status === 'transitioning' || this.state.status === 'exiting';
  }

  private drain() { return this.ports.drain ? this.ports.drain() : drainDemo(); }

  private newGeneration(): { generation: number; signal: AbortSignal } {
    this.abort?.abort();
    this.abort = new AbortController();
    const generation = this.state.generation + 1;
    return { generation, signal: this.abort.signal };
  }

  goTo(index: number): Promise<DemoAck> {
    if (this.disposed || this.exitPromise || this.state.status === 'exited') {
      return Promise.resolve({ ok: false, reason: 'Walkthrough has ended.' });
    }
    const target = Math.max(0, Math.min(index, this.total - 1));
    const { generation, signal } = this.newGeneration();
    // Leaving an unconfirmed checkpoint: its data is not the committed chapter.
    if (this.state.status === 'awaiting') this.dataClean = false;
    this.set({ generation, targetIndex: target, status: 'transitioning', error: null, failedOp: null });
    const run = this.chain.then(() => this.execute(target, generation, signal));
    this.chain = run.catch(() => undefined);
    return run;
  }

  retry(): Promise<DemoAck> {
    const target = this.state.targetIndex ?? Math.max(0, this.state.committedIndex);
    this.dataClean = false;
    return this.goTo(target);
  }

  private async execute(target: number, generation: number, signal: AbortSignal): Promise<DemoAck> {
    // Never start while an older generation's port work is still running.
    await this.drain();
    const isCurrent = () =>
      !this.disposed && !signal.aborted && this.state.generation === generation && this.state.status === 'transitioning';
    const ctx: PortContext = { isCurrent, signal };
    if (!isCurrent()) return { ok: false, reason: 'superseded' };

    const fail = (reason: string): DemoAck => {
      this.dataClean = false;
      if (isCurrent()) this.set({ status: 'error', error: reason, failedOp: 'goto', targetIndex: target });
      return { ok: false, reason };
    };
    const step = async (fn: () => Promise<DemoAck>, writes: boolean): Promise<DemoAck | null> => {
      if (!isCurrent()) return { ok: false, reason: 'superseded' };
      if (writes) this.dataClean = false; // before the write, never after
      let ack: DemoAck;
      try { ack = await fn(); } catch (e) { ack = { ok: false, reason: (e as Error)?.message || 'Unexpected error' }; }
      if (!isCurrent()) return { ok: false, reason: 'superseded' };
      return ack.ok ? null : fail(ack.reason || 'Step refused');
    };

    const from = this.state.committedIndex;
    const incremental = this.dataClean && from >= 0 && target === from + 1;

    let r = await step(() => this.ports.clearUi(ctx), false);
    if (r) return r;
    if (!incremental) {
      r = await step(() => this.ports.resetBaseline(ctx), true);
      if (r) return r;
    }
    r = await step(() => this.ports.persistChapter(target, incremental ? from : null, ctx), true);
    if (r) return r;
    r = await step(() => this.ports.refetch(target, ctx), false);
    if (r) return r;
    r = await step(() => this.ports.switchActor(target, ctx), false);
    if (r) return r;
    r = await step(() => this.ports.applyUi(target, ctx), false);
    if (r) return r;

    if (this.ports.isCheckpoint(target)) {
      // Data equals the PRE-decision state; not clean for an incremental +1.
      this.dataClean = false;
      this.set({ status: 'awaiting', committedIndex: target, targetIndex: null, error: null, failedOp: null });
      return { ok: true };
    }
    this.dataClean = true;
    this.set({ status: 'idle', committedIndex: target, targetIndex: null, error: null, failedOp: null });
    return { ok: true };
  }

  /** Called once the real decision is confirmed from saved data. */
  confirmCheckpoint(): Promise<DemoAck> {
    if (this.state.status !== 'awaiting') return Promise.resolve({ ok: false, reason: 'No decision is pending.' });
    const target = this.state.committedIndex;
    const { generation, signal } = this.newGeneration();
    this.set({ generation, status: 'transitioning', targetIndex: target });
    const run = this.chain.then(async () => {
      await this.drain();
      const isCurrent = () => !this.disposed && !signal.aborted && this.state.generation === generation;
      const ctx = { isCurrent, signal };
      this.dataClean = false;
      let ack: DemoAck;
      try { ack = await this.ports.finalizeCheckpoint(target, ctx); } catch (e) { ack = { ok: false, reason: (e as Error).message }; }
      if (ack.ok) {
        try { ack = await this.ports.refetch(target, ctx); } catch (e) { ack = { ok: false, reason: (e as Error).message }; }
      }
      if (!isCurrent()) return { ok: false, reason: 'superseded' };
      if (!ack.ok) {
        this.set({ status: 'error', error: ack.reason ?? 'Could not finish the chapter', failedOp: 'goto', targetIndex: target });
        return ack;
      }
      this.dataClean = true;
      this.set({ status: 'idle', targetIndex: null });
      return { ok: true };
    });
    this.chain = run.catch(() => undefined);
    return run;
  }

  exit(): Promise<DemoAck> {
    if (this.state.status === 'exited') return Promise.resolve({ ok: true });
    if (this.exitPromise) return this.exitPromise;
    const { generation } = this.newGeneration();
    this.set({ generation, status: 'exiting', error: null, failedOp: null });
    this.exitPromise = (async () => {
      await this.chain;
      await this.drain();
      let ack: DemoAck;
      try { ack = await this.ports.restore(); } catch (e) { ack = { ok: false, reason: (e as Error)?.message || 'Restore failed' }; }
      if (!ack.ok) {
        this.exitPromise = null; // allow a deliberate retry
        this.set({ status: 'error', failedOp: 'exit', error: `Could not restore the sample project: ${ack.reason ?? 'unknown'}` });
        return ack;
      }
      this.disposed = true;
      this.set({ status: 'exited', targetIndex: null });
      return { ok: true };
    })();
    return this.exitPromise;
  }

  abandon() {
    this.abort?.abort();
    this.disposed = true;
    this.set({ status: 'exited', generation: this.state.generation + 1 });
  }
}
